import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithRedirect } from "./utils/apiUtils.ts";
import endpoints, { baseURL } from "data/api/v1/endpoints.ts";
import {
	GetManageableStoresResponse,
	GetManageableStoresResponseData,
	GetStoreSettingsResponse,
	GetStoreSettingsResponseData,
	GetStoresResponse,
	GetStoresResponseData,
	UpdateStoreSettingsRequest,
	UpdateStoreSettingsRequestParameters,
	UpdateStoreSettingsResponse,
	UpdateStoreSettingsResponseData
} from "../../types/api/stores/stores.ts";

interface UpdateStoreSettingsMutationRequest extends UpdateStoreSettingsRequestParameters {
	request: UpdateStoreSettingsRequest;
}

export const storesApiSlice = createApi({
	reducerPath: "stores",
	baseQuery: baseQueryWithRedirect(baseURL),
	tagTypes: ["Stores", "StoreSettings"],
	endpoints: (builder) => ({
		getStores: builder.query<GetStoresResponseData, void>({
			query: () => endpoints.stores.getStores(),
			transformResponse: (response: GetStoresResponse) => response?.data,
			providesTags: ["Stores"]
		}),
		getManageableStores: builder.query<GetManageableStoresResponseData, void>({
			query: () => endpoints.stores.getManageableStores(),
			transformResponse: (response: GetManageableStoresResponse) => response?.data,
			providesTags: ["Stores"]
		}),
		getStoreSettings: builder.query<GetStoreSettingsResponseData, string>({
			query: (storeId: string) => ({
				url: endpoints.stores.getStoreSettings(storeId)
			}),
			transformResponse: (response: GetStoreSettingsResponse) => response?.data,
			providesTags: (_result, _error, storeId) => [{ type: "StoreSettings", id: storeId }]
		}),
		updateStoreSettings: builder.mutation<UpdateStoreSettingsResponseData, UpdateStoreSettingsMutationRequest>({
			query: ({ storeId, request }) => ({
				url: endpoints.stores.updateStoreSettings(storeId),
				method: "PUT",
				dataType: "json",
				headers: {
					"Content-type": "application/json; charset=UTF-8"
				},
				body: request
			}),
			transformResponse: (response: UpdateStoreSettingsResponse) => response?.data,
			invalidatesTags: (_result, _error, { storeId }) => ["Stores", { type: "StoreSettings", id: storeId }]
		})
	})
});

export const {
	useLazyGetStoresQuery,
	useLazyGetManageableStoresQuery,
	useGetStoreSettingsQuery,
	useUpdateStoreSettingsMutation
} = storesApiSlice;
